// Core State Management - UI Store
// Location: src/core/store/uiStore.ts

import { create } from 'zustand';
import type { UIState, Notification, ToastType } from '../../types/ui.types';

export const useUIStore = create<UIState>()((set, get) => ({
  // State
  isSidebarOpen: false,
  isModalOpen: false,
  modalContent: null,
  notifications: [],
  toast: null,
  isGlobalLoading: false,

  // Sidebar actions
  toggleSidebar: () => set((state) => ({
    isSidebarOpen: !state.isSidebarOpen
  })),

  openSidebar: () => set({ isSidebarOpen: true }),

  closeSidebar: () => set({ isSidebarOpen: false }),

  // Modal actions
  openModal: (content) => set({
    isModalOpen: true,
    modalContent: content
  }),

  closeModal: () => set({
    isModalOpen: false,
    modalContent: null
  }),

  // Notification actions
  addNotification: (notification): number => {
    const id = Date.now();
    const newNotification: Notification = {
      id,
      timestamp: new Date().toISOString(),
      read: false,
      ...notification
    };

    set((state) => ({
      notifications: [newNotification, ...state.notifications],
    }));

    return id;
  },

  removeNotification: (id: number) => {
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    }));
  },

  markNotificationAsRead: (id: number) => {
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.id === id ? { ...n, read: true } : n
      ),
    }));
  },

  markAllNotificationsAsRead: () => {
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, read: true })),
    }));
  },

  clearAllNotifications: () => set({ notifications: [] }),

  getUnreadCount: (): number => {
    const { notifications } = get();
    return notifications.filter((n) => !n.read).length;
  },

  // Toast actions
  showToast: (message: string, type: ToastType = 'info', duration: number = 3000) => {
    const id = Date.now();
    set({
      toast: {
        id,
        message,
        type,
        duration
      }
    });

    // Auto-hide toast after duration
    if (duration > 0) {
      setTimeout(() => {
        const currentToast = get().toast;
        if (currentToast?.id === id) {
          set({ toast: null });
        }
      }, duration);
    }
  },

  hideToast: () => set({ toast: null }),

  // Global loading state
  setGlobalLoading: (isLoading: boolean) => set({ isGlobalLoading: isLoading }),
}));
